/**
 * Хэрэглэгчийн эрх — админ РОЛЬ ТУС БҮРЭЭР тохируулна.
 *
 * ЗОХИОМЖИЙН ШИЙДВЭР (эхний хувилбар «ойлгомжгүй» гэж буцаагдсан)
 *
 *  1. Чагттай нүд, дүүргэсэн pill БИШ. Дүүргэсэн pill нь «сонгогдсон
 *     шүүлтүүр» шиг уншигдаж, асаалттай/унтраалттайг заадаггүй байв.
 *  2. Үйлдэл бүрийн доор ТАЙЛБАР. «Засах» гэдэг нь юуг засахыг
 *     каталогийн `hint` хэлнэ — админ таамаглахгүй.
 *  3. Дээд талд ТОВЧООН мөр: хэдэн ажилтан, хэдэн эрх, хэд өөрчилсөн.
 *     Өмнө нь «би юу өөрчилснөө» харах ямар ч зам байгаагүй.
 *  4. Модуль бүрд «бүгдийг асаах/хаах», роль бүрд «анхдагч руу
 *     буцаах» — нэг нэгээр 6 удаа дарах шаардлагагүй.
 *  5. Хайлт ба «зөвхөн өөрчилсөн» шүүлтүүр — 21 модуль, 64 эрхийг
 *     гүйлгэж хайх шаардлагагүй.
 *  6. Бүлэг нурааж болно — ажиллаж буй хэсэгтээ л төвлөрнө.
 *
 * ⚠️ Каталог (модуль, үйлдэл, шошго, тайлбар) нь `@boots/domain`-оос
 *    ирнэ, сервер сүлжээгээр явуулдаггүй. Сервер, веб хоёр НЭГ
 *    жагсаалт уншдаг тул зөрөх боломжгүй.
 *
 * ⚠️ Хоёр дүрэм UI-д ТҮГЖЭЭТЭЙ, учир нь сервер ч мөн адил татгалзана:
 *    · ADMIN — өөрийгөө системээс гаргах зам үлдээхгүй
 *    · DIRECTOR_READONLY — бичих үйлдэл (`readOnlyGuard` бүхнийг хаана)
 *
 * ⚠️ Эрх хасахад хэрэглэгч дахин нэвтрэх ШААРДЛАГАГҮЙ — сервер эрхийг
 *    токеноос биш, кэшээс уншина. Дараагийн хүсэлтээс хүчинтэй.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatQty } from '@boots/domain';
import {
  DEFAULT_GRANTS,
  PERM_ACTION_LABEL,
  PERM_GROUPS,
  PERM_GROUP_LABEL,
  PERM_MODULES,
  PERM_ROLES,
  isLockedRole,
  isReadOnlyRole,
  permKey,
  type PermModuleDef,
  type PermRole,
} from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { ROLE_LABEL } from '../../lib/types';
import { Button, Empty, ErrorNote, Input, OkNote, Spinner, Tabs } from '../../components/ui';

interface PermissionsPayload {
  /** Роль → бодит эрхийн жагсаалт */
  effective: Record<string, string[]>;
  /** Роль → кодын анхдагч */
  defaults: Record<string, string[]>;
  /** Роль → админы зөрүү (`{ 'items.price': false }`) */
  overrides: Record<string, Record<string, boolean>>;
  /** Роль → идэвхтэй ажилтны тоо */
  users: Record<string, number>;
}



export function PermissionsTab() {
  const qc = useQueryClient();
  const [role, setRole] = useState<PermRole>('WAREHOUSE');
  const [search, setSearch] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['role-permissions'],
    queryFn: () => api<Envelope<PermissionsPayload>>('/ref/permissions'),
  });

  function done(message: string) {
    setNote(message);
    setFailed(null);
    return qc.invalidateQueries({ queryKey: ['role-permissions'] });
  }

  const save = useMutation({
    mutationFn: (v: { permissions: string[]; allowed: boolean }) =>
      api('/ref/permissions', { method: 'PUT', body: { role, ...v } }),
    onSuccess: (_r, v) =>
      done(
        v.permissions.length === 1
          ? `${v.allowed ? 'Нээлээ' : 'Хаалаа'}`
          : `${formatQty(v.permissions.length)} эрх ${v.allowed ? 'нээлээ' : 'хаалаа'}`,
      ),
    onError: (e: Error) => { setFailed(e.message); setNote(null); },
  });

  const reset = useMutation({
    mutationFn: () => api('/ref/permissions/reset', { method: 'POST', body: { role } }),
    onSuccess: () => done('Анхдагч руу буцаалаа'),
    onError: (e: Error) => { setFailed(e.message); setNote(null); },
  });

  const busy = save.isPending || reset.isPending;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return PERM_MODULES;
    return PERM_MODULES.filter((m) =>
      [m.label, m.path ?? '', m.key, ...m.actions.map((a) => PERM_ACTION_LABEL[a.action])]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [search]);

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const { effective, defaults, overrides, users } = q.data.data;
  const granted = new Set(effective[role] ?? []);
  const base = new Set(defaults[role] ?? []);
  const changed = overrides[role] ?? {};
  const changedCount = Object.keys(changed).length;

  const locked = isLockedRole(role);
  const readOnly = isReadOnlyRole(role);

  /** Тухайн үйлдлийг энэ ролид өөрчилж болох эсэх. */
  function editable(action: string): boolean {
    if (locked) return false;
    if (readOnly && action !== 'view') return false;
    return true;
  }

  const visible = filtered.filter((m) =>
    !onlyChanged || m.actions.some((a) => permKey(m.key, a.action) in changed),
  );

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        value={role}
        onChange={(r) => { setRole(r); setNote(null); setFailed(null); }}
        tabs={PERM_ROLES.map((r) => ({ key: r, label: ROLE_LABEL[r], count: users[r] ?? 0 }))}
      />

      <RoleSummary
        role={role}
        users={users[role] ?? 0}
        granted={granted.size}
        total={DEFAULT_GRANTS[role].length}
        changed={changedCount}
        locked={locked}
        readOnly={readOnly}
        busy={busy}
        onReset={() => reset.mutate()}
      />

      {failed ? <ErrorNote>{failed}</ErrorNote> : null}
      {note ? <OkNote>{note}</OkNote> : null}

      {/* ─────────── Хайлт, шүүлтүүр ─────────── */}
      <div className="flex flex-wrap items-center gap-2.5">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Хуудас, үйлдлээр хайх…"
          aria-label="Эрх хайх"
          className="max-w-72"
        />
        <button
          type="button"
          onClick={() => setOnlyChanged((v) => !v)}
          aria-pressed={onlyChanged}
          disabled={changedCount === 0}
          className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3.5 py-1
            text-[12.5px] transition disabled:cursor-not-allowed disabled:opacity-45 sm:min-h-0 ${
            onlyChanged ? 'border-transparent bg-accent-100 text-accent-700' : 'border-hair text-ink-2'
          }`}
        >
          Зөвхөн өөрчилсөн
          <span className="num opacity-70">{formatQty(changedCount)}</span>
        </button>
        <span className="ml-auto text-[11.5px] text-ink-3">
          {formatQty(visible.length)} / {formatQty(PERM_MODULES.length)} хуудас
        </span>
      </div>

      {visible.length === 0 ? <Empty>Тохирох хуудас олдсонгүй</Empty> : null}

      {/* ─────────── Бүлэг → модуль ─────────── */}
      {PERM_GROUPS.map((group) => {
        const modules = visible.filter((m) => m.group === group);
        if (modules.length === 0) return null;
        const isCollapsed = collapsed.has(group);

        return (
          <section key={group} className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() =>
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(group)) next.delete(group); else next.add(group);
                  return next;
                })
              }
              aria-expanded={!isCollapsed}
              className="flex min-h-11 w-full items-center gap-2 text-[10.5px] tracking-[0.14em]
                text-ink-3 transition hover:text-ink-2 sm:min-h-0"
            >
              <span aria-hidden className="text-[9px]">{isCollapsed ? '▶' : '▼'}</span>
              {PERM_GROUP_LABEL[group]}
              <span className="num opacity-70">{formatQty(modules.length)}</span>
              <span className="h-px flex-1 bg-hair" />
            </button>

            {isCollapsed ? null : modules.map((m) => (
              <ModuleCard
                key={m.key}
                module={m}
                granted={granted}
                base={base}
                changed={changed}
                busy={busy}
                editable={editable}
                onToggle={(permissions, allowed) => save.mutate({ permissions, allowed })}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------ Товчоо
function RoleSummary({
  role, users, granted, total, changed, locked, readOnly, busy, onReset,
}: {
  role: PermRole;
  users: number;
  granted: number;
  total: number;
  changed: number;
  locked: boolean;
  readOnly: boolean;
  busy: boolean;
  onReset: () => void;
}) {
  return (
    <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-3.5">
      <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
        <h3 className="text-base font-semibold">{ROLE_LABEL[role]}</h3>
        <span className="num text-[12.5px] text-ink-2">{formatQty(users)} идэвхтэй ажилтан</span>
        <span className="num text-[12.5px] text-ink-2">
          {formatQty(granted)} эрх
          {locked ? '' : ` · анхдагч ${formatQty(total)}`}
        </span>
        {changed > 0 ? (
          <span className="num rounded-full bg-alert-bg px-2.5 py-[3px] text-[11px] text-alert-ink">
            {formatQty(changed)} өөрчилсөн
          </span>
        ) : (
          <span className="text-[11.5px] text-ink-3">анхдагчаараа</span>
        )}
        {!locked && changed > 0 ? (
          <Button variant="ghost" onClick={onReset} disabled={busy} className="ml-auto">
            Анхдагч руу буцаах
          </Button>
        ) : null}
      </div>

      {locked ? (
        <Note tone="warn">
          Админ бүх эрхтэй бөгөөд <strong>өөрчлөгдөхгүй</strong>. Өөрийн эрхээ хасвал
          системд буцаж орох зам үлдэхгүй.
        </Note>
      ) : readOnly ? (
        <Note tone="warn">
          Захирал <strong>зөвхөн харна</strong>. Бичих үйлдлийг сервер эрхээс үл хамааран
          хаадаг (<span className="num">readOnlyGuard</span>) тул зөвхөн «Харах» асаагдана.
        </Note>
      ) : (
        <Note tone="info">
          Өөрчлөлт <strong>шууд хүчинтэй</strong> — тухайн ажилтан дахин нэвтрэх шаардлагагүй.
          Хаасан эрх нь цэснээс алга болж, API талд ч 403 өгнө.
        </Note>
      )}
    </section>
  );
}

function Note({ tone, children }: { tone: 'info' | 'warn'; children: React.ReactNode }) {
  return (
    <p
      className={`rounded-well px-3.5 py-2.5 text-[12.5px] leading-[1.5] ${
        tone === 'warn' ? 'bg-alert-bg text-alert-ink' : 'bg-well text-ink-2'
      }`}
    >
      {children}
    </p>
  );
}

// ------------------------------------------------------------ Модуль
function ModuleCard({
  module: m, granted, base, changed, busy, editable, onToggle,
}: {
  module: PermModuleDef;
  granted: ReadonlySet<string>;
  base: ReadonlySet<string>;
  changed: Record<string, boolean>;
  busy: boolean;
  editable: (action: string) => boolean;
  onToggle: (permissions: string[], allowed: boolean) => void;
}) {
  const keys = m.actions.map((a) => permKey(m.key, a.action));
  const on = keys.filter((k) => granted.has(k));
  const editableKeys = m.actions.filter((a) => editable(a.action)).map((a) => permKey(m.key, a.action));
  const allOn = editableKeys.length > 0 && editableKeys.every((k) => granted.has(k));

  return (
    <div className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-3.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[13.5px] font-medium text-pretty">{m.label}</span>
        <span className="num text-[11px] text-ink-3">{m.path ?? 'гар утас'}</span>
        <span className="num ml-auto text-[12px] text-ink-2">
          {formatQty(on.length)} / {formatQty(keys.length)}
        </span>
        {editableKeys.length > 0 ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onToggle(editableKeys, !allOn)}
            className="inline-flex min-h-11 items-center rounded-full border border-hair px-3 py-0.5
              text-[11.5px] text-ink-2 transition hover:bg-well disabled:opacity-45 sm:min-h-0"
          >
            {allOn ? 'Бүгдийг хаах' : 'Бүгдийг нээх'}
          </button>
        ) : null}
      </div>

      <div className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
        {m.actions.map((a) => {
          const key = permKey(m.key, a.action);
          return (
            <ActionCell
              key={key}
              label={PERM_ACTION_LABEL[a.action]}
              hint={a.hint}
              on={granted.has(key)}
              /**
               * ⚠️ «Өөрчилсөн» гэдэг нь DB-д зөрүүний мөр байгаа эсэх.
               *    Бодит утга анхдагчтай тэнцүү байж болно — захирлын
               *    бичих эрхийг сервер хүчээр хасдаг.
               */
              overridden={key in changed}
              baseOn={base.has(key)}
              disabled={busy || !editable(a.action)}
              onToggle={(next) => onToggle([key], next)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ Нэг үйлдэл
function ActionCell({
  label, hint, on, overridden, baseOn, disabled, onToggle,
}: {
  label: string;
  hint?: string;
  on: boolean;
  overridden: boolean;
  baseOn: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onToggle(!on)}
      title={overridden ? `Анхдагч: ${baseOn ? 'нээлттэй' : 'хаалттай'}` : undefined}
      className={`flex items-start gap-2.5 rounded-well border px-3 py-2.5 text-left transition ${
        disabled ? 'cursor-not-allowed opacity-45' : 'hover:border-accent-300'
      } ${on ? 'border-accent-300 bg-accent-100' : 'border-hair bg-well'}`}
    >
      {/* Чагт — асаалттай/унтраалттайг НЭГ харцаар. Дүүргэсэн pill нь
          «сонгогдсон шүүлтүүр» шиг уншигддаг байв. */}
      <span
        aria-hidden
        className={`mt-px grid size-[18px] shrink-0 place-items-center rounded-[5px] border text-[11px] ${
          on ? 'border-transparent bg-accent-fill text-white' : 'border-hair bg-panel text-transparent'
        }`}
      >
        ✓
      </span>

      <span className="flex min-w-0 flex-col gap-0.5">
        <span className={`flex items-center gap-1.5 text-[13px] ${on ? 'text-accent-800' : 'text-ink-2'}`}>
          {label}
          {overridden ? (
            <span
              aria-label="анхдагчаас өөрчилсөн"
              className="size-1.5 shrink-0 rounded-full bg-alert"
            />
          ) : null}
        </span>
        {hint ? <span className="text-[11px] text-ink-3 text-pretty">{hint}</span> : null}
      </span>
    </button>
  );
}
